import FormTemplateRenderProps from '@data-driven-forms/react-form-renderer/dist/cjs/form-template-render-props';
import { FormOptions } from '@data-driven-forms/react-form-renderer/dist/cjs/renderer-context';
import { FORM_ERROR } from 'final-form';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useUpdatePassword, useVerifyResetToken, VerifyResetTokenResponse } from '../api';
import {
  HcpFormRenderer,
  HcpFormTemplate,
  HcpRequestStateUI,
  PublicLayout,
  ResourceForgotPasswordAuthLayoutHeader,
  ResourceContent
} from '../components';
import { ForgotPasswordAuthLayout } from '../components/ForgotPasswordAuthLayout';
import image from '../images/img_Hero.jpg';
import { AnyType, GeneralObject } from '../interfaces';
import { AppRootState, navigateToLoginPostReset } from '../redux';
import {
  clientApplicationNameSelector,
  clientApplicationVersionSelector,
  getConfigurationDataKey,
  getResetPasswordFormSchema,
  ROUTE_PATHS
} from '../utils';

export const ResetPasswordView = () => {
  const { token } = useParams<{ token: string }>();
  const { mutate: verifyResetToken, loading } = useVerifyResetToken({});
  const [verifyTokenResult, setVerifyTokenResult] = useState<VerifyResetTokenResponse>();
  const didCallValidation = verifyTokenResult !== null;
  const isValidToken =
    verifyTokenResult?.tokenStatus?.toLowerCase() === 'recovery' ||
    verifyTokenResult?.tokenStatus?.toLowerCase() === 'password_reset';
  const applicationName = useSelector(clientApplicationNameSelector);
  const isLoginImage = useSelector(
    (state: AppRootState) => ((state.app?.entities?.resources as AnyType) ?? {})['login.image'] as AnyType
  );
  const verifyToken = useCallback(async () => {
    const result = await verifyResetToken({ applicationName: applicationName, resetToken: token });
    setVerifyTokenResult(result);
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const renderErrorMessage = useCallback(() => {
    // NOTE: using a span here since the wrapper uses `p` to wrap the error.
    return (
      <>
        <span className='d-block'>
          <ResourceContent contentKey='reset-password.expired' />
        </span>
        <span className='d-block pt-2'>
          Click <Link to={ROUTE_PATHS.forgotPassword}>HERE</Link> to get a new link and reset your password.
        </span>
      </>
    );
  }, []);

  return (
    <PublicLayout fluid className='authlayout_custom_image'>
      <ForgotPasswordAuthLayout>
        {isValidToken && (
          <ResourceForgotPasswordAuthLayoutHeader contentKey={getConfigurationDataKey('reset-password.title')}>
            <ResourceContent contentKey={getConfigurationDataKey('reset-password.body')} />
          </ResourceForgotPasswordAuthLayoutHeader>
        )}

        <HcpRequestStateUI
          isError={didCallValidation && isValidToken === false}
          errorUIEnabled
          errorTitle='RESET PASSWORD LINK EXPIRED'
          errorText={renderErrorMessage()}
          isLoading={loading || !didCallValidation}
          loadingUIEnabled
          loadingText='Verifying token'>
          <ResetPasswordForm token={token} />
        </HcpRequestStateUI>
      </ForgotPasswordAuthLayout>
    </PublicLayout>
  );
};

interface ResetPasswordFormProps {
  token: string;
}

const ResetPasswordForm = ({ token }: ResetPasswordFormProps) => {
  const dispatch = useDispatch();
  const { mutate: updatePassword } = useUpdatePassword({});

  const applicationName = useSelector(clientApplicationNameSelector);
  const applicationVersion = useSelector(clientApplicationVersionSelector);

  const onSubmit = async (values: GeneralObject) => {
    const result = await updatePassword({
      applicationName: applicationName,
      applicationVersion: applicationVersion,
      resetToken: token,
      newPassword: String(values.new_password)
    });

    if ((result?.errors?.length ?? 0) > 0) return { [FORM_ERROR]: result.errors?.map((e) => e.description).join(' ') };

    dispatch(navigateToLoginPostReset());
  };

  return (
    <HcpFormRenderer
      contentKey={getConfigurationDataKey('form.reset-password')}
      FormTemplate={ResetPasswordTemplate}
      noErrorMessage
      onSubmit={onSubmit}
      schemaMethod={getResetPasswordFormSchema}
    />
  );
};

const ResetPasswordTemplate = (props: FormTemplateRenderProps) => {
  return (
    <HcpFormTemplate
      {...props}
      renderControls={(formProps: FormOptions) => {
        const formState = formProps.getState();
        const { submitError, submitting, hasValidationErrors } = formState;
        const renderTypeOfSubmitError = () => {
          if (submitError?.includes('Password requirements were not met')) {
            // Check if the password requirement error is present
            return (
              <div>
                <p className='text-danger' style={{ maxWidth: '400px' }}>
                  The Password that have been entered does not meet the requirements.
                </p>
                <div
                  style={{
                    width: '100%',
                    color: '#212529',
                    fontSize: 14,
                    fontFamily: 'Barlow',
                    fontWeight: '600',
                    wordWrap: 'break-word'
                  }}>
                  Password requirements:
                </div>
                <ul>
                  <li>At least 12 characters in length</li>
                  <li>A lowercase letter (a-z)</li>
                  <li>An uppercase letter (A-Z)</li>
                  <li>A number (i.e. 0-9)</li>
                  <li>A symbol</li>
                  <li>No parts of your username</li>
                  <li>Does not include your first name, does not include your last name</li>
                  <li>Your password cannot be any of your last 5 password(s)</li>
                  <li>At least 1 day(s) must have elapsed since you last changed your password</li>
                </ul>
              </div>
            );
          }
          // Fallback for other errors
          return <p className='text-danger'>{submitError}</p>;
        };

        return (
          <Row>
            {submitError && renderTypeOfSubmitError()}
            <Col xs={12}>
              <Button
                className='px-3 py-2'
                type='submit'
                variant='secondary'
                disabled={submitting || hasValidationErrors}>
                {submitting ? 'Submitting...' : 'Set Password'}
                <i className='fa-thin fa-paper-plane-top ms-2'></i>
              </Button>
            </Col>
          </Row>
        );
      }}
    />
  );
};
